import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow } from '../../schemas/gameshow.js';

import { toPointer } from '../../core/issues.js';

export const endingNode: Rule<Gameshow> = {
  code: 'gameshow.ending',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const endings = input.nodes
      .map((node, idx) => ({ node, idx }))
      .filter(({ node }) => node.nodeType === 'ending');

    if (endings.length !== 1) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.ending.count',
        severity: 'error',
        message: `Expected exactly 1 ending node, found ${endings.length}. Use a single neutral ending with showGameStats.`,
        path,
        pointer: toPointer(path),
        currentValue: endings.length,
        constraint: { kind: 'range', detail: 'exactly 1' },
      });
    }

    for (const { node, idx } of endings) {
      const summary = node.endingSummary;
      if (!summary) {
        const path: (string | number)[] = ['nodes', idx, 'endingSummary'];
        issues.push({
          code: 'gameshow.ending.missing-summary',
          severity: 'error',
          message: `Ending node '${node.id}' missing 'endingSummary'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'endingSummary required' },
        });
        continue;
      }
      if (typeof summary.summaryText !== 'string' || !summary.summaryText.trim()) {
        const path: (string | number)[] = ['nodes', idx, 'endingSummary', 'summaryText'];
        issues.push({
          code: 'gameshow.ending.missing-summary-text',
          severity: 'error',
          message: `Ending node '${node.id}': 'endingSummary.summaryText' must be a non-empty string.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: summary.summaryText,
          constraint: { kind: 'shape', detail: 'non-empty string required' },
        });
      }
      if (summary.showGameStats !== true) {
        const path: (string | number)[] = ['nodes', idx, 'endingSummary', 'showGameStats'];
        issues.push({
          code: 'gameshow.ending.show-game-stats',
          severity: 'error',
          message: `Ending node '${node.id}': 'endingSummary.showGameStats' must be true so the scoreboard renders.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: summary.showGameStats,
          constraint: { kind: 'enum', allowedValues: ['true'] },
        });
      }
    }

    return issues;
  },
};
